import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { updateCardType } from "../api/cardsApi";
import { CardType } from "../types";

const formSchema = z.object({
  type: z.string().min(1, "Type name is required"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EditCardTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardType: CardType | null;
  onSaved?: () => void;
}

export default function EditCardTypeDialog({ open, onOpenChange, cardType, onSaved }: EditCardTypeDialogProps) {
  const [submitting, setSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      type: cardType?.type ?? "",
      description: cardType?.description ?? "",
    },
  });

  React.useEffect(() => {
    if (cardType) {
      form.reset({ type: cardType.type ?? "", description: cardType.description ?? "" });
    }
  }, [cardType, form]);

  async function handleSubmit(values: FormValues) {
    if (!cardType?.id) return;
    setSubmitting(true);
    try {
      await updateCardType(cardType.id, {
        type: values.type,
        description: values.description || undefined,
      });
      toast.success("Card type updated successfully");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to update card type");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-md w-full">
        <DialogHeader>
          <DialogTitle>Edit Card Type</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Type Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="description"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      placeholder="Enter description (optional)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
